// CINE-1 Task B — pure helper: derive the true terminal outcome from the
// wire's winner_slot (now projected by the backend on every PlayerView frame).
//
// Single source of truth consumed by BOTH WinOverlay and the SR announce block
// in MatchScreen so the visual and screen-reader text can never disagree.

import type { MatchStatus, PlayerView } from '../../game/PlayerViewSchema'

// The full set of wire terminal statuses (decisive OR draw). The true
// per-player outcome is derived from winner_slot, not from which member
// of this set was received.
const WIRE_TERMINAL_STATUSES = new Set<string>([
  'completed',
  'abandoned',
  'timeout_resolved',
  'stalemate_resolved',
])

/**
 * Derive the end-of-match outcome for the local player.
 *
 * Priority order:
 *  1. Back-compat: 'won'/'lost'/'drawn' legacy matchStatus strings map directly
 *     (kept for test fixtures and any future wire path that uses them).
 *  2. winner_slot present on playerView: compare against your_slot to get
 *     'win'/'lose', or return 'draw' when winner_slot === null.
 *  3. Defensive fallback (no playerView, or winner_slot absent): 'complete'.
 */
export function deriveTerminalOutcome(
  matchStatus: MatchStatus | string | null,
  playerView: PlayerView | null | undefined,
): 'win' | 'lose' | 'draw' | 'complete' {
  // Back-compat paths — legacy status strings still used in unit-test fixtures.
  if (matchStatus === 'won') return 'win'
  if (matchStatus === 'lost') return 'lose'
  if (matchStatus === 'drawn') return 'draw'

  // For all other terminal statuses, use winner_slot when present.
  if (
    matchStatus !== null &&
    WIRE_TERMINAL_STATUSES.has(matchStatus) &&
    playerView != null &&
    'winner_slot' in playerView &&
    (playerView.winner_slot === 0 ||
      playerView.winner_slot === 1 ||
      playerView.winner_slot === null)
  ) {
    if (playerView.winner_slot === null) return 'draw'
    return playerView.winner_slot === playerView.your_slot ? 'win' : 'lose'
  }

  // Defensive: winner_slot absent or playerView unavailable.
  return 'complete'
}
